import { Box, CircularProgress } from '@mui/material';
import type { LabelEntityType } from '@vy/types';
import type { FC, MouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDialog } from '../../../../hooks/use-dialog';
import { useLabels, useUpdateEntityLabels } from '../../../../hooks/requests/use-labels';
import { runMutation } from '../../../../utils/vy-mutation';
import { LabelsChips } from './LabelsChips';
import { LabelsPopover } from './LabelsPopover';

const POPOVER_ESTIMATED_HEIGHT = 300;

type EntityLabelsCellProps = {
  entityType: LabelEntityType;
  entityId: string;
  labelIds: string[];
  onUpdateSuccess?: () => Promise<void>;
};

export const EntityLabelsCell: FC<EntityLabelsCellProps> = ({
  entityType,
  entityId,
  labelIds,
  onUpdateSuccess,
}) => {
  const anchorRef = useRef<HTMLElement | null>(null);
  const [openUpward, setOpenUpward] = useState(false);
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const [localLabelIds, setLocalLabelIds] = useState(labelIds);

  useEffect(() => {
    setLocalLabelIds(labelIds);
  }, [labelIds]);

  const { data: labelsData, isLoading: isLoadingLabels } = useLabels();
  const updateLabelsMutation = useUpdateEntityLabels(entityType, entityId);

  const labels = labelsData?.labels ?? [];

  const assignedLabels = useMemo(() => {
    return labels.filter((label) => localLabelIds.includes(label.id));
  }, [labels, localLabelIds]);

  const handleOpenPopover = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      anchorRef.current = event.currentTarget;
      const rect = event.currentTarget.getBoundingClientRect();
      setOpenUpward(rect.bottom + POPOVER_ESTIMATED_HEIGHT > window.innerHeight);
      handleOpen();
    },
    [handleOpen],
  );

  const handleCellClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (!isDialogOpen) {
        handleOpenPopover(event);
      }
    },
    [isDialogOpen, handleOpenPopover],
  );

  const handleToggleLabel = useCallback(
    (labelId: string) => {
      const isSelected = localLabelIds.includes(labelId);
      const newLabelIds = isSelected ? localLabelIds.filter((id) => id !== labelId) : [...localLabelIds, labelId];

      setLocalLabelIds(newLabelIds);

      void runMutation(
        updateLabelsMutation,
        { labelIds: newLabelIds },
        {
          pending: 'Updating labels...',
          success: 'Labels updated',
          error: 'Failed to update labels',
        },
        {
          onSuccess: async () => {
            await onUpdateSuccess?.();
          },
        },
      );
    },
    [localLabelIds, updateLabelsMutation, onUpdateSuccess],
  );

  const handleLabelCreated = useCallback(
    (labelId: string) => {
      const newLabelIds = [...localLabelIds, labelId];

      setLocalLabelIds(newLabelIds);

      void runMutation(
        updateLabelsMutation,
        { labelIds: newLabelIds },
        {
          pending: 'Adding label...',
          success: 'Label added',
          error: 'Failed to add label',
        },
        {
          onSuccess: async () => {
            await onUpdateSuccess?.();
          },
        },
      );
    },
    [localLabelIds, updateLabelsMutation, onUpdateSuccess],
  );

  if (isLoadingLabels) {
    return <CircularProgress size={16} />;
  }

  return (
    <Box
      onClick={handleCellClick}
      sx={{
        overflow: 'hidden',
        maxWidth: '100%',
        cursor: 'pointer',
        width: '100%',
        minHeight: 32,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <LabelsChips labels={assignedLabels} />
      <LabelsPopover
        open={isDialogOpen}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        entityType={entityType}
        labels={labels}
        selectedLabelIds={localLabelIds}
        onToggle={handleToggleLabel}
        onLabelCreated={handleLabelCreated}
        isUpdating={updateLabelsMutation.isLoading}
        openUpward={openUpward}
      />
    </Box>
  );
};
